import React, { useState, useMemo, useRef, useEffect } from "react";
import "./index.scss";

export const ClientMultiSelect = ({
  clients,
  selectedClients,
  onChange,
  loading = false,
  placeholder = "Search clients...",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedIds = useMemo(
    () => new Set(selectedClients.map((c) => c.id)),
    [selectedClients]
  );

  const filteredClients = useMemo(() => {
    return clients
      .filter((client) => !selectedIds.has(client.id || client._id))
      .filter((client) => {
        const searchLower = searchTerm.toLowerCase();
        const email = (client.email || "").toLowerCase();
        const fullName = `${client.firstName || ""} ${client.lastName || ""}`.toLowerCase();
        return email.includes(searchLower) || fullName.includes(searchLower);
      });
  }, [clients, selectedIds, searchTerm]);

  const handleSelectClient = (client) => {
    const newClient = {
      id: client.id || client._id,
      clientId: client.id || client._id,
      clientEmail: client.email,
      firstName: client.firstName,
      lastName: client.lastName,
    };
    onChange([...selectedClients, newClient]);
    setSearchTerm("");
  };

  const handleRemoveClient = (clientId) => {
    onChange(selectedClients.filter((c) => c.id !== clientId));
  };

  const handleSelectAll = () => {
    const allClients = clients.map((client) => ({
      id: client.id || client._id,
      clientId: client.id || client._id,
      clientEmail: client.email,
      firstName: client.firstName,
      lastName: client.lastName,
    }));
    onChange(allClients);
    setShowDropdown(false);
  };

  const handleClearAll = () => {
    onChange([]);
  };

  return (
    <div className="client-multi-select" ref={containerRef}>
      {/* Selected clients chips */}
      {selectedClients.length > 0 && (
        <div className="selected-clients">
          {selectedClients.map((client) => (
            <span key={client.id} className="client-chip">
              {client.firstName} {client.lastName}
              <button
                type="button"
                onClick={() => handleRemoveClient(client.id)}
                aria-label={`Remove ${client.firstName}`}
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Selected count and clear all */}
      {selectedClients.length > 0 && (
        <div className="selection-info">
          <span className="selected-count">
            {selectedClients.length} client{selectedClients.length > 1 ? "s" : ""} selected
          </span>
          <button type="button" className="clear-all" onClick={handleClearAll}>
            Clear all
          </button>
        </div>
      )}

      {/* Search input */}
      <input
        type="text"
        className="custom-input bg-transparent search-input"
        placeholder={placeholder}
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setShowDropdown(true);
        }}
        onFocus={() => setShowDropdown(true)}
      />

      {/* Dropdown */}
      {showDropdown && (
        <div className="dropdown-menu show">
          {/* Select All option */}
          {filteredClients.length > 0 && selectedClients.length < clients.length && (
            <button
              type="button"
              className="dropdown-item select-all"
              onClick={handleSelectAll}
            >
              Select All ({clients.length} clients)
            </button>
          )}

          {loading ? (
            <div className="dropdown-item disabled">Loading clients...</div>
          ) : filteredClients.length === 0 ? (
            <div className="dropdown-item disabled">
              {searchTerm ? "No clients found" : "All clients selected"}
            </div>
          ) : (
            filteredClients.slice(0, 50).map((client) => (
              <button
                key={client.id || client._id}
                type="button"
                className="dropdown-item"
                onClick={() => handleSelectClient(client)}
              >
                <span className="client-name">
                  {client.firstName} {client.lastName}
                </span>
                <span className="client-email">{client.email}</span>
              </button>
            ))
          )}

          {filteredClients.length > 50 && (
            <div className="dropdown-item disabled">
              ...and {filteredClients.length - 50} more. Type to filter.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientMultiSelect;
